/* =====================================================================
 * StatTile — a single headline number.
 *
 * Deliberately not a chart: one value over one window has no shape to
 * plot, and a sparkline beside a number that already says "128" adds
 * ink without adding an answer.
 * ===================================================================== */

import React from 'react';
import styled from 'styled-components';

const Tile = styled.div`
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 14px;
    border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  }
`;

const Label = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: inline-flex;
  align-items: center;
  gap: 6px;

  svg {
    width: 13px;
    height: 13px;
  }
`;

const Value = styled.strong`
  font-size: 1.625rem;
  font-weight: 650;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-variant-numeric: tabular-nums;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.375rem;
  }
`;

const Unit = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-left: 3px;
`;

const Hint = styled.span`
  min-height: 1em;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

export interface StatTileProps {
  label: string;
  value: string | number;
  unit?: string;
  hint?: string;
  title?: string;
  icon?: React.ComponentType;
}

export const StatTile: React.FC<StatTileProps> = ({
  label,
  value,
  unit,
  hint,
  title,
  icon: Icon,
}) => (
  <Tile title={title}>
    <Label>
      {Icon && <Icon aria-hidden />}
      {label}
    </Label>
    <Value>
      {value}
      {unit && <Unit>{unit}</Unit>}
    </Value>
    {hint && <Hint>{hint}</Hint>}
  </Tile>
);
